import useOperationalClock from '../core/shifts/useOperationalClock';
import useTenantTimeZone from '../components/common/useTenantTimeZone';
import { resolveShiftTimeZone, formatShiftInstant } from '../core/shifts/shiftTimeZone';
import { saveWeeklyAvailability } from '../components/worker/weeklyAvailabilityService';
import WeeklyAvailability from '../components/worker/WeeklyAvailability';
import { isStaleUnfinishedShift } from "../core/shifts/workerShiftTiming";
import WorkerWorkspace from '../components/worker/WorkerWorkspace';
import { getVisibilityStage } from '../core/shifts/shiftDailyBoard';
import ShiftCard from "../components/worker/WorkerShiftCard";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";

import WorkerOnboarding from "./WorkerOnboarding";
import { evaluateWorkerCompliance } from "../utils/complianceEngine";

import {
  requestPoolShift,

  acceptDirectOffer as acceptShiftOffer,

   acceptPlannerAssignment,

  declinePlannerAssignment,

  declineDirectOffer as declineShiftOffer,

  activateShift as setShiftActivated,

  startShiftTracking as setShiftTracking,

  markShiftArrived as setShiftArrived,

  completeShift as setShiftCompleted,

  withdrawPoolRequest as declineShift,
} from "../core/shifts/shiftService";

import {
  roleColorFor,
} from "../core/shifts/shiftPresentation";

import {
  computeShiftActivationUrgency as computeShiftLifecycle,
  computeShiftCountdown,
} from "../core/shifts/shiftLifecycle";
import useWorkerShifts from "../core/data/useWorkerShifts";

import {
  doc,
  getDoc,
  updateDoc,
  collection,
  addDoc,
  serverTimestamp,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";

import {
  ensurePermissions,
  startTrackingForShift,
  stopTracking,
} from "../geoTracker";
import getWorkerOperationalState from "../utils/workers/getWorkerOperationalState";
import getWorkerCompliance from "../utils/compliance/getWorkerCompliance";

const EXPIRED_GRACE_MINUTES = 15;
const MOBILE_BREAKPOINT = 9999;

const AVAILABILITY_STATUS_OPTIONS = [
  { key: "available", label: "Available" },
  { key: "off_today", label: "Off Today" },
  { key: "vacation", label: "Vacation" },
  { key: "sick", label: "Sick" },
];

function availabilityStatusLabel(v) {
  const found = AVAILABILITY_STATUS_OPTIONS.find((x) => x.key === v);
  return found?.label || "Available";
}

function toMillis(v) {
  if (!v) return NaN;
  try {
    if (typeof v?.toDate === "function") return v.toDate().getTime();
    if (typeof v === "number") return v;
    return new Date(v).getTime();
  } catch {
    return NaN;
  }
}

function shiftPaymentStatus(shift) {
  const raw = String(shift?.paymentStatus || "").toLowerCase();
  if (raw === "paid") return "paid";
  return "pending";
}

function roleKey(v) {
  return String(v || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_")
    .replace(/_+/g, "_");
}

function isCancelled(shift) {
  return (
    String(shift?.status || "").toLowerCase() === "cancelled" ||
    shift?.active === false
  );
}

function hasShiftEnded(shift, nowMs = Date.now()) {
  const endMs = toMillis(shift?.endTime);
  return Number.isFinite(endMs) && nowMs >= endMs;
}

function hasShiftStarted(shift, nowMs = Date.now()) {
  const startMs = toMillis(shift?.startTime);
  return Number.isFinite(startMs) && nowMs >= startMs;
}

function wasRecentlyUpdated(shift, nowMs = Date.now(), windowHours = 24) {
  const updatedMs = toMillis(shift?.updatedAt);
  const createdMs = toMillis(shift?.createdAt);

  if (!Number.isFinite(updatedMs)) return false;

  const recentCutoff = nowMs - windowHours * 60 * 60 * 1000;
  if (updatedMs < recentCutoff) return false;

  if (Number.isFinite(createdMs)) return updatedMs - createdMs > 60 * 1000;
  return true;
}

function formatDateTime(v, timeZone) {
  return formatShiftInstant(v, timeZone, {month:'numeric', day:'numeric', year:'numeric', hour:'numeric', minute:'2-digit'});
}

function formatElapsed(startTime, nowMs) {
  const start = toMillis(startTime);
  if (!Number.isFinite(start)) return "";
  const diff = nowMs - start;
  if (diff <= 0) return "";
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hrs <= 0) return `Started ${mins} min ago`;
  return `Started ${hrs} hr${hrs > 1 ? "s" : ""} ${rem} min ago`;
}

function formatStartsIn(startTime, nowMs) {
  const start = toMillis(startTime);
  if (!Number.isFinite(start)) return "";
  const diff = start - nowMs;
  if (diff <= 0) return "";
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hrs <= 0) return `Starts in ${mins} min`;
  return `Starts in ${hrs} hr${hrs > 1 ? "s" : ""} ${rem} min`;
}

function isSameDayMs(ms, dayStartMs, nextDayStartMs) {
  return Number.isFinite(ms) && ms >= dayStartMs && ms < nextDayStartMs;
}
function progressWidthForStatus(status, ended = false) {
  if (ended) return "100%";

  const s = String(status || "").toLowerCase();

  if (s === "completed") return "100%";
  if (s === "arrived") return "100%";
  if (s === "tracking") return "82%";
  if (s === "activated") return "68%";
  if (s === "assigned") return "38%";
  if (s === "offered") return "24%";

  return "14%";
}

function progressColorForStatus(status, ended = false) {
  if (ended) return "linear-gradient(90deg, #64748b, #94a3b8)";

  const s = String(status || "").toLowerCase();

  if (s === "completed") return "linear-gradient(90deg, #16a34a, #22c55e)";
  if (s === "arrived") return "linear-gradient(90deg, #16a34a, #22c55e)";
  if (s === "tracking") return "linear-gradient(90deg, #7c3aed, #8b5cf6)";
  if (s === "activated") return "linear-gradient(90deg, #2563eb, #3b82f6)";
  if (s === "assigned") return "linear-gradient(90deg, #1d4ed8, #60a5fa)";
  if (s === "offered") return "linear-gradient(90deg, #ea580c, #fb923c)";

  return "linear-gradient(90deg, #64748b, #94a3b8)";
}

function overlapsRange(aStart, aEnd, bStart, bEnd) {
  const a1 = toMillis(aStart);
  const a2 = toMillis(aEnd);
  const b1 = toMillis(bStart);
  const b2 = toMillis(bEnd);

  if (
    !Number.isFinite(a1) ||
    !Number.isFinite(a2) ||
    !Number.isFinite(b1) ||
    !Number.isFinite(b2)
  ) {
    return false;
  }

  return a1 < b2 && b1 < a2;
}
function cardShellStyle() {
  return {
    width: "100%",
    maxWidth: 430,
    margin: "0 auto",
    border: "1px solid #d8e1ea",
    borderRadius: 18,
    boxShadow: "0 8px 22px rgba(15,23,42,.05)",
    overflow: "hidden",
    background: "#ffffff",
    boxSizing: "border-box",
  };
}


function cardHeaderStyle() {
  return {
    padding: "16px 18px 14px",
    borderBottom: "1px solid #e9eef5",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    background: "#fbfcfe",
  };
}

function countPillStyle() {
  return {
    minWidth: 34,
    height: 34,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    fontSize: 14,
    color: "#334155",
    background: "#f8fafc",
    border: "1px solid #d7dee8",
  };
}

function actionReasonPill(text, tone = "neutral") {
  const tones = {
    neutral: {
      bg: "#f8fafc",
      border: "#d8e1ea",
      color: "#64748b",
    },
    warning: {
      bg: "#fff7ed",
      border: "#fdba74",
      color: "#9a3412",
    },
    danger: {
      bg: "#fee2e2",
      border: "#fca5a5",
      color: "#b91c1c",
    },
    info: {
      bg: "#eff6ff",
      border: "#93c5fd",
      color: "#1d4ed8",
    },
  };

  const t = tones[tone] || tones.neutral;

  return (
    <span
      style={{
        background: t.bg,
        border: `1px solid ${t.border}`,
        color: t.color,
        borderRadius: 10,
        padding: "9px 14px",
        fontWeight: 900,
        fontSize: 13,
      }}
    >
      {text}
    </span>
  );
}

export default function WorkerDashboard({ user, userProfile, profile }) {
  const workerProfile = userProfile || profile || {};
const role = String(workerProfile?.role || "").toLowerCase();
const compliance = getWorkerCompliance(workerProfile);

const registrationApproved =
  workerProfile?.registrationStatus === "approved" ||
  workerProfile?.registrationApproved === true ||
  workerProfile?.canAccessOnboarding === true;

const requiresOnboarding =
  registrationApproved &&
  ["worker", "nurse", "caregiver"].includes(role) &&
  compliance.canWork !== true;

const tenantId = String(
  workerProfile?.tenantId ||
    workerProfile?.tenantKey ||
    ""
)
  .trim()
  .toLowerCase();

  console.log("WORKER QUERY SCOPE AUDIT", {
  authUid: auth.currentUser?.uid || "",
  workerProfileUid: workerProfile?.uid,
  workerProfileTenantId: workerProfile?.tenantId,
  workerProfileTenantKey: workerProfile?.tenantKey,
  resolvedTenantId: tenantId,
});

console.log("WORKER FIREBASE RUNTIME AUDIT", {
  authProjectId:
    auth?.app?.options?.projectId || "",
  firestoreProjectId:
    db?.app?.options?.projectId || "",
  authAppName:
    auth?.app?.name || "",
  firestoreAppName:
    db?.app?.name || "",
});

const facilityIdRaw =
  workerProfile?.facilityId ||
  workerProfile?.primaryFacilityId ||
  (Array.isArray(workerProfile?.facilityIds)
    ? workerProfile.facilityIds[0]
    : "") ||
  "";

const facilityId = String(
  facilityIdRaw || ""
)
  .trim()
  .toLowerCase();

const authUid =
  auth.currentUser?.uid || "";

const workerUid = authUid;

const workerDocId =
  workerProfile?.uid ||
  workerProfile?.id ||
  workerProfile?.userId ||
  authUid;

  const [myShifts, setMyShifts] = useState([]);
  const [facilityShifts, setFacilityShifts] = useState([]);
  const tenantTimeZone = useTenantTimeZone(tenantId);
  const [facilityTimeSettings, setFacilityTimeSettings] = useState({});
  const shiftTimeZone = shift => resolveShiftTimeZone(shift, facilityTimeSettings[shift?.facilityId || facilityId], {timeZone: tenantTimeZone});
  const [myOffers, setMyOffers] = useState([]);
  const shiftFacilityKey = [...new Set([...myShifts, ...facilityShifts, ...myOffers].map(shift => shift.facilityId).filter(Boolean))].sort().join('|');
  useEffect(() => {
    let active = true;
    const ids = shiftFacilityKey ? shiftFacilityKey.split('|') : [];
    Promise.all(ids.map(async id => {
      try { const snap = await getDoc(doc(db, 'facilityProfiles', id)); const data = snap.data();
        return [id, data ? {timeZone:data.timeZone, timezone:data.timezone} : null];
      } catch { return [id, null]; }
    })).then(entries => { if (active) setFacilityTimeSettings(previous => ({...previous, ...Object.fromEntries(entries)})); });
    return () => { active = false; };
  }, [shiftFacilityKey]);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined"
      ? window.innerWidth < MOBILE_BREAKPOINT
      : false
  );

  const now = useOperationalClock();
  const [activationWindowHours, setActivationWindowHours] = useState(2);
  const [facilityGeo, setFacilityGeo] = useState({
    lat: null,
    lng: null,
    radius: 200,
  });

  const [listenerError, setListenerError] = useState("");
const [actionFeedback, setActionFeedback] = useState({});

const [availabilityStatus, setAvailabilityStatus] = useState(
  workerProfile?.availabilityStatus || "available"
);

const [availabilitySaving, setAvailabilitySaving] = useState(false);

const [actionBusyId, setActionBusyId] = useState("");
const [expandedShiftId, setExpandedShiftId] = useState("");


  const insideCountRef = useRef(0);
  const lastHeroRef = useRef(null);

  useEffect(() => {
  setAvailabilityStatus(workerProfile?.availabilityStatus || "available");
}, [workerProfile?.availabilityStatus]);


  const workerStatus = String(workerProfile?.status || "").toLowerCase();
  const isWorkerActive = compliance.canWork === true;
  function haversineMeters(lat1, lng1, lat2, lng2) {
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) ** 2;

    return 2 * R * Math.asin(Math.sqrt(a));
  }

  const normalizeCreds = useCallback((creds) => {
    const arr = Array.isArray(creds) ? creds : [];

    return arr
      .map((c) => {
        if (c && typeof c === "object") {
          return {
            type: String(c.type || c.credential || ""),
            verified: c.verified === true,
            expiresAt: c.expiresAt ?? null,
          };
        }

        if (typeof c === "string") {
          return {
            type: c,
            verified: true,
            expiresAt: null,
          };
        }

        return null;
      })
      .filter(Boolean);
  }, []);

  const isCredValidForRole = useCallback((cred, requiredRole) => {
    const roleMatch = roleKey(cred?.type) === roleKey(requiredRole);
    const verified = cred?.verified === true;

    const notExpired = !cred?.expiresAt
      ? true
      : (() => {
          try {
            return cred.expiresAt.toDate().getTime() > Date.now();
          } catch {
            return true;
          }
        })();

    return roleMatch && verified && notExpired;
  }, []);

const workerCreds = useMemo(() => {
  const creds =
    workerProfile?.credentials ||
    Object.values(workerProfile?.credentialMap || {}) ||
    Object.values(workerProfile?.documents || {});

  return normalizeCreds(creds);
}, [
  normalizeCreds,
  workerProfile?.credentials,
  workerProfile?.credentialMap,
  workerProfile?.documents,
]);

const hasValidCredentialForRole = useCallback(
  (requiredRole) => {
    const requiredRoleKey = roleKey(requiredRole);

    const profileRoleKeys = [
      workerProfile?.primaryRole,
      workerProfile?.position,
      workerProfile?.workerType,
      workerProfile?.licenseType,
      workerProfile?.roleType,
      ...(Array.isArray(workerProfile?.positions)
        ? workerProfile.positions
        : []),
      ...(Array.isArray(workerProfile?.approvedPositions)
        ? workerProfile.approvedPositions
        : []),
    ]
      .map(roleKey)
      .filter(Boolean);

    const roleMatchesProfile =
      profileRoleKeys.includes(requiredRoleKey);

    const roleMatchesCredential = workerCreds.some((cred) =>
      isCredValidForRole(cred, requiredRole)
    );

    const professionalLicense =
      workerProfile?.credentialMap?.professional_license ||
      workerProfile?.documents?.professional_license ||
      {};

    const professionalLicenseApproved =
      String(professionalLicense.status || "").toLowerCase() === "approved" &&
      professionalLicense.verified === true;

    return (
      roleMatchesCredential ||
      (roleMatchesProfile && professionalLicenseApproved)
    );
  },
  [
    workerCreds,
    isCredValidForRole,
    workerProfile?.primaryRole,
    workerProfile?.position,
    workerProfile?.workerType,
    workerProfile?.licenseType,
    workerProfile?.roleType,
    workerProfile?.positions,
    workerProfile?.approvedPositions,
    workerProfile?.credentialMap,
    workerProfile?.documents,
  ]
);

  const getAssignedUid = useCallback((shift) => {
    if (!shift) return null;
    if (shift.assignedWorkerUid) return shift.assignedWorkerUid;

    if (
      Array.isArray(shift.assignedWorkerUids) &&
      shift.assignedWorkerUids.length > 0
    ) {
      return shift.assignedWorkerUids[0];
    }

    return null;
  }, []);

  const getMode = useCallback((shift) => {
    return String(shift?.mode || shift?.assignMode || "pool").toLowerCase();
  }, []);

 const isExpiredShift = useCallback((shift) => {
  const status = String(shift?.status || "").toLowerCase();

  // Never hide worked/completed/payroll history shifts
  if (["completed", "ended"].includes(status)) return false;

  // Do not auto-hide assigned/live shifts just because end time passed.
  // They must be completed by planner or worker flow.
  if (["assigned", "activated", "tracking", "arrived"].includes(status)) return false;

  const endMs = toMillis(shift?.endTime);
  if (!Number.isFinite(endMs)) return false;

  const cutoff = Date.now() - EXPIRED_GRACE_MINUTES * 60 * 1000;
  return endMs < cutoff;
}, []);


  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    if (typeof window !== "undefined") {
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }
    return undefined;
  }, []);
useEffect(() => {
  if (!facilityId) return;

  (async () => {
    try {
      const snap = await getDoc(doc(db, "facilityProfiles", facilityId));
      if (!snap.exists()) return;

      const facilityData = snap.data() || {};
      setFacilityTimeSettings(previous => ({...previous, [facilityId]: {timeZone: facilityData.timeZone, timezone: facilityData.timezone}}));

      const activationHours = Number(facilityData.activationWindowHours ?? 2);
      setActivationWindowHours(Number.isFinite(activationHours) ? activationHours : 2);

      const facilityLat = Number(facilityData.facilityLat);
      const facilityLng = Number(facilityData.facilityLng);
      const geoRadius = Number(facilityData.geoRadiusMeters ?? 200);

      setFacilityGeo({
        lat: Number.isFinite(facilityLat) ? facilityLat : null,
        lng: Number.isFinite(facilityLng) ? facilityLng : null,
        radius: Number.isFinite(geoRadius) ? geoRadius : 200,
      });
    } catch (e) {
      console.error("Failed to load facility settings:", e);
    }
  })();
}, [facilityId]);


/*
 * =========================================================
 * CANONICAL WORKER SHIFT REAL-TIME DATA
 * =========================================================
 *
 * WorkerDashboard no longer owns independent Firestore
 * queries for:
 *
 * - direct assignment
 * - assignment arrays
 * - pool requests
 * - whole-facility shifts
 *
 * useWorkerShifts is now the Worker 360 read boundary.
 *
 * The existing local state is intentionally retained during
 * this migration so the rest of WorkerDashboard does not
 * have to change at the same time.
 */

const {
  myShifts: canonicalMyShifts,
  poolShifts: canonicalPoolShifts,
  offeredShifts: canonicalOfferedShifts,
  visibleShifts: canonicalVisibleShifts,

  loading: workerShiftsLoading,
  error: workerShiftsDataError,
  channelHealth: workerShiftChannelHealth,
  summary: workerShiftSummary,
} = useWorkerShifts({
  tenantId,
  facilityId,
  workerUid,

  enabled: Boolean(
    workerUid &&
      tenantId
  ),

  debugLabel:
    "WORKER 360 SHIFTS",
});

/*
 * =========================================================
 * TEMPORARY COMPATIBILITY BRIDGE
 * =========================================================
 *
 * Existing WorkerDashboard presentation logic still uses:
 *
 *   myShifts
 *   facilityShifts
 *
 * Keep those names temporarily while their data now comes
 * from the canonical worker-safe controller.
 *
 * facilityShifts deliberately receives ONLY worker-visible
 * pool shifts — never the complete facility schedule.
 */

useEffect(() => {
  setMyShifts(
    Array.isArray(
      canonicalMyShifts
    )
      ? canonicalMyShifts
      : []
  );

  setFacilityShifts(
    Array.isArray(
      canonicalPoolShifts
    )
      ? canonicalPoolShifts
      : []
  );

  setMyOffers(
    Array.isArray(
      canonicalOfferedShifts
    )
      ? canonicalOfferedShifts
      : []
  );

  setListenerError(
    workerShiftsDataError
      ?.message || ""
  );
}, [
  canonicalMyShifts,
  canonicalPoolShifts,
  canonicalOfferedShifts,
  workerShiftsDataError,
]);

useEffect(() => {
  console.log(
    "WORKER 360 REAL-TIME VISIBILITY",
    {
      workerUid,
      tenantId,
      facilityId,

      loading:
        workerShiftsLoading,

      summary:
        workerShiftSummary,

      channelHealth:
        workerShiftChannelHealth,

      visibleShiftCount:
        canonicalVisibleShifts
          ?.length || 0,

      canonicalOfferCount:
        canonicalOfferedShifts
          ?.length || 0,
    }
  );
}, [
  workerUid,
  tenantId,
  facilityId,
  workerShiftsLoading,
  workerShiftSummary,
  workerShiftChannelHealth,
  canonicalVisibleShifts,
  canonicalOfferedShifts,
]);

const blockingMyShifts = useMemo(
  () =>
    (myShifts || []).filter((s) => {
      const status = String(s.status || "").toLowerCase();

      const assignedToMe =
        s.assignedWorkerUid === workerUid ||
        s.workerUid === workerUid ||
        s.workerId === workerUid ||
        (Array.isArray(s.assignedWorkerUids) &&
          s.assignedWorkerUids.includes(workerUid));

      return (
        assignedToMe &&
        ["assigned", "activated", "tracking", "arrived"].includes(status)
      );
    }),
  [myShifts, workerUid]
);

  const overlapsBlockingShift = useCallback(
    (shift) => {
      const sStart = toMillis(shift.startTime);
      const sEnd = toMillis(shift.endTime);
      if (!Number.isFinite(sStart) || !Number.isFinite(sEnd)) return false;
      console.log("BLOCK CHECK", {
        shiftId: shift.id,
        blockingMyShifts,
      });

      return blockingMyShifts.some((mine) => {
        const mStart = toMillis(mine.startTime);
        const mEnd = toMillis(mine.endTime);
        if (!Number.isFinite(mStart) || !Number.isFinite(mEnd)) return false;
        if (mine.id === shift.id) return false;
        return sStart < mEnd && mStart < sEnd;
      });
    },
    [blockingMyShifts]
  );



const handlePoolRequest = async (shiftId) => {
  if (actionBusyId === `request-${shiftId}`) return;

  try {
    setActionBusyId(`request-${shiftId}`);

    const shift = facilityShifts.find((s) => s.id === shiftId);
    if (!shift) return;

    if (overlapsBlockingShift(shift)) {
      alert("Cannot request — overlaps with another assigned shift.");
      return;
    }

    typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{phase:'pending',label:"Requesting shift",beforeStage:getVisibilityStage([...(myShifts||[]),...(myOffers||[]),...(facilityShifts||[])].find(item=>item.id===shiftId)||{})}}));
await requestPoolShift(shiftId, workerUid);
typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{...previous[shiftId],phase:'waiting'}}));

    alert("Requested. Waiting for planner approval.");
  } catch (err) {
typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{...previous[shiftId],phase:'failed',error:err?.message}}));
    alert(err?.message || "Request failed");
  } finally {
    setActionBusyId("");
  }
};

const handleAcceptOffer = async (shiftId) => {
  if (actionBusyId === `accept-${shiftId}`) return;

  try {
    setActionBusyId(`accept-${shiftId}`);

    const shift = myOffers.find((s) => s.id === shiftId);
    if (!shift) return;

    if (overlapsBlockingShift(shift)) {
      alert("Cannot accept — overlaps with another assigned shift.");
      return;
    }
      typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{phase:'pending',label:"Accepting offer",beforeStage:getVisibilityStage([...(myShifts||[]),...(myOffers||[]),...(facilityShifts||[])].find(item=>item.id===shiftId)||{})}}));
await acceptShiftOffer(shiftId, auth.currentUser.uid);
typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{...previous[shiftId],phase:'waiting'}}));

    setMyOffers((prev) =>
      (prev || []).filter((s) => {
        if (s.id === shiftId) return false;
        return !overlapsRange(s.startTime, s.endTime, shift.startTime, shift.endTime);
      })
    );

    alert("Action returned. Check the updated shift state for confirmation.");
  } catch (e) {
typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{...previous[shiftId],phase:'failed',error:e?.message}}));
    alert(e?.message || "Accept failed.");
  } finally {
    setActionBusyId("");
  }
};

const handleDeclineOffer = async (shiftId) => {
  if (!shiftId || !workerUid) return;

  if (
    actionBusyId ===
    `decline-${shiftId}`
  ) {
    return;
  }

  try {
    setActionBusyId(
      `decline-${shiftId}`
    );

    const shift =
      (myOffers || []).find(
        (item) =>
          item?.id === shiftId
      );

    if (!shift) {
      alert(
        "This offer is no longer available."
      );
      return;
    }

    const offeredWorkerUids =
      Array.isArray(
        shift?.offeredWorkerUids
      )
        ? shift.offeredWorkerUids
        : [];

    if (
      !offeredWorkerUids.includes(
        workerUid
      )
    ) {
      alert(
        "This shift is no longer offered to you."
      );
      return;
    }

    const mode =
      getMode(shift);

    if (mode !== "direct") {
      alert(
        "This is no longer a direct offer."
      );
      return;
    }

    const status =
      String(
        shift?.status || ""
      )
        .trim()
        .toLowerCase();

    if (
      ![
        "offered",
        "open",
      ].includes(status)
    ) {
      alert(
        "This offer is no longer available."
      );
      return;
    }

    const confirmed =
      window.confirm(
        "Decline this shift offer?"
      );

    if (!confirmed) {
      return;
    }

    console.log(
      "WORKER DECLINE DIRECT OFFER",
      {
        shiftId,
        workerUid,
        tenantId:
          shift?.tenantId,
        facilityId:
          shift?.facilityId,
        offeredWorkerUids,
      }
    );

    /*
     * true means:
     *
     * - if other direct offers remain,
     *   keep the shift OFFERED;
     *
     * - if this was the final offer,
     *   return the shift to the POOL
     *   as OPEN.
     */
    typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{phase:'pending',label:"Declining offer",beforeStage:getVisibilityStage([...(myShifts||[]),...(myOffers||[]),...(facilityShifts||[])].find(item=>item.id===shiftId)||{})}}));
await declineShiftOffer(
      shiftId,
      workerUid,
      true
    );
typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{...previous[shiftId],phase:'waiting'}}));

    setMyOffers(
      (prev) =>
        (prev || []).filter(
          (item) =>
            item?.id !== shiftId
        )
    );

    alert("Offer declined.");
  } catch (err) {
typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{...previous[shiftId],phase:'failed',error:err?.message}}));
    console.error(
      "Decline direct offer failed:",
      {
        shiftId,
        workerUid,
        code: err?.code,
        message: err?.message,
        error: err,
      }
    );

    alert(
      err?.message ||
        "Unable to decline this offer."
    );
  } finally {
    setActionBusyId("");
  }
};

const handleAcceptAssignedShift = async (shift) => {
  if (!shift?.id || !workerUid) return;

  if (
    actionBusyId ===
    `accept-assigned-${shift.id}`
  ) {
    return;
  }

  try {
    setActionBusyId(
      `accept-assigned-${shift.id}`
    );

    const assignedUid =
      getAssignedUid(shift);

    if (!assignedUid) {
      alert(
        "This shift does not have an assigned worker."
      );
      return;
    }

    if (assignedUid !== workerUid) {
      alert(
        "This shift is assigned to another worker."
      );
      return;
    }

    const status = String(
      shift.status || ""
    )
      .trim()
      .toLowerCase();

    if (status !== "assigned") {
      alert(
        "This shift is no longer awaiting acceptance."
      );
      return;
    }

    /*
     * Direct offers must continue through
     * acceptShiftOffer().
     *
     * This handler is only for a shift that
     * has already been assigned by a planner.
     */
    const mode = getMode(shift);

    const alreadyAccepted =
      shift.assignmentAccepted === true ||
      shift.acceptedByWorker === true ||
      !!shift.acceptedAt;

    if (alreadyAccepted) {
      setExpandedShiftId(shift.id);
      return;
    }

    /*
     * Check for overlap against another
     * assigned/live shift before calling
     * the centralized Firestore action.
     */
    if (overlapsBlockingShift(shift)) {
      const conflictingShift =
        blockingMyShifts.find(
          (mine) =>
            mine.id !== shift.id &&
            overlapsRange(
              mine.startTime,
              mine.endTime,
              shift.startTime,
              shift.endTime
            )
        );

      if (conflictingShift) {
        alert(
          "Cannot accept — this shift overlaps another assignment."
        );
        return;
      }
    }

    console.log(
      "WORKER ACCEPT PLANNER ASSIGNMENT",
      {
        shiftId: shift.id,
        workerUid,
        mode,
        status,
        tenantId: shift.tenantId,
        facilityId: shift.facilityId,
        assignmentReason:
          shift.assignmentReason,
      }
    );

    typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shift.id]:{phase:'pending',label:"Accepting assignment",beforeStage:getVisibilityStage([...(myShifts||[]),...(myOffers||[]),...(facilityShifts||[])].find(item=>item.id===shift.id)||{})}}));
await acceptPlannerAssignment(
      shift.id,
      workerUid
    );
typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shift.id]:{...previous[shift.id],phase:'waiting'}}));

    setExpandedShiftId(shift.id);

    alert("Action returned. Check the updated shift state for confirmation.");
  } catch (err) {
typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shift.id]:{...previous[shift.id],phase:'failed',error:err?.message}}));
    console.error(
      "Accept assigned shift failed:",
      {
        shiftId: shift?.id,
        workerUid,
        code: err?.code,
        message: err?.message,
        error: err,
      }
    );

    alert(
      err?.message ||
        "Unable to accept this assignment."
    );
  } finally {
    setActionBusyId("");
  }
};

const handleDeclineAssignedShift = async (shift) => {
  if (!shift?.id || !workerUid) return;

  if (
    actionBusyId ===
    `decline-assigned-${shift.id}`
  ) {
    return;
  }

  const status = String(
    shift.status || ""
  )
    .trim()
    .toLowerCase();

  if (status !== "assigned") {
    alert(
      "This shift is no longer awaiting your response."
    );
    return;
  }

  const assignedUid =
    getAssignedUid(shift);

  if (!assignedUid) {
    alert(
      "This shift does not have an assigned worker."
    );
    return;
  }

  if (assignedUid !== workerUid) {
    alert(
      "This shift is assigned to another worker."
    );
    return;
  }

  /*
   * Direct-offer decline must continue through
   * declineShiftOffer().
   *
   * This handler is only for a shift that has
   * already been assigned by the Planner.
   */
  const mode = getMode(shift);

  const reason =
    window.prompt(
      "Why are you declining this assignment?",
      "Unable to work this shift"
    ) ||
    "Unable to work this shift";

  try {
    setActionBusyId(
      `decline-assigned-${shift.id}`
    );

    console.log(
      "WORKER DECLINE PLANNER ASSIGNMENT",
      {
        shiftId: shift.id,
        workerUid,
        tenantId: shift.tenantId,
        facilityId: shift.facilityId,
        mode,
        status,
        reason,
      }
    );

    typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shift.id]:{phase:'pending',label:"Declining assignment",beforeStage:getVisibilityStage([...(myShifts||[]),...(myOffers||[]),...(facilityShifts||[])].find(item=>item.id===shift.id)||{})}}));
await declinePlannerAssignment(
      shift.id,
      workerUid,
      reason
    );
typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shift.id]:{...previous[shift.id],phase:'waiting'}}));

    setExpandedShiftId("");

    alert(
      "Assignment declined and returned to the pool."
    );
  } catch (err) {
typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shift.id]:{...previous[shift.id],phase:'failed',error:err?.message}}));
    console.error(
      "Decline assigned shift failed:",
      {
        shiftId: shift?.id,
        workerUid,
        tenantId: shift?.tenantId,
        facilityId: shift?.facilityId,
        code: err?.code,
        message: err?.message,
        error: err,
      }
    );

    alert(
      err?.message ||
        "Unable to decline this assignment."
    );
  } finally {
    setActionBusyId("");
  }
};

const handleStartTracking = async (shiftId) => {
  if (!shiftId || !workerUid) return;

  try {
    setActionBusyId(`tracking-${shiftId}`);

    typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{phase:'pending',label:"Starting tracking",beforeStage:getVisibilityStage([...(myShifts||[]),...(myOffers||[]),...(facilityShifts||[])].find(item=>item.id===shiftId)||{})}}));
await setShiftTracking(shiftId, workerUid);
typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{...previous[shiftId],phase:'waiting'}}));
  } catch (err) {
typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{...previous[shiftId],phase:'failed',error:err?.message}}));
    console.error("Start tracking failed:", err);
    alert(err?.message || "Unable to mark On My Way.");
  } finally {
    setActionBusyId("");
  }
};

 const handleActivate = async (shiftId) => {
  try {
    typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{phase:'pending',label:"Activating shift",beforeStage:getVisibilityStage([...(myShifts||[]),...(myOffers||[]),...(facilityShifts||[])].find(item=>item.id===shiftId)||{})}}));
await setShiftActivated(
      shiftId,
      workerUid,
      activationWindowHours
    );
typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{...previous[shiftId],phase:'waiting'}}));
  } catch (err) {
typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{...previous[shiftId],phase:'failed',error:err?.message}}));
    alert(
      err?.message ||
        "Activate failed"
    );
  }
};

  const handleArrived = async (shiftId) => {
    try {
      typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{phase:'pending',label:"Recording arrival",beforeStage:getVisibilityStage([...(myShifts||[]),...(myOffers||[]),...(facilityShifts||[])].find(item=>item.id===shiftId)||{})}}));
await setShiftArrived(shiftId, workerUid);
typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{...previous[shiftId],phase:'waiting'}}));
    } catch (err) {
typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{...previous[shiftId],phase:'failed',error:err?.message}}));
      alert(err?.message || "Arrived failed");
    }
  };

const handleComplete = async (shiftId) => {
  try {
    typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{phase:'pending',label:"Completing shift",beforeStage:getVisibilityStage([...(myShifts||[]),...(myOffers||[]),...(facilityShifts||[])].find(item=>item.id===shiftId)||{})}}));
await setShiftCompleted(shiftId, workerUid);
typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{...previous[shiftId],phase:'waiting'}}));
  } catch (err) {
typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shiftId]:{...previous[shiftId],phase:'failed',error:err?.message}}));
    alert(err?.message || "Complete failed");
  }
};

const canActivate = (shift) => {
  const start = toMillis(shift.startTime);
    const end = toMillis(shift.endTime);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || now >= end) return false;

  const activationMs =
    activationWindowHours * 60 * 60 * 1000;

  const activationOpenAt = start - activationMs;

  const accepted =
    shift.assignmentAccepted === true ||
    shift.acceptedByWorker === true ||
    !!shift.acceptedAt;

  return (
    isWorkerActive &&
    accepted &&
    now >= activationOpenAt &&
    String(shift.status || "").toLowerCase() === "assigned" &&
    !shift.activatedAt &&
    !isExpiredShift(shift) &&
    !isCancelled(shift)
  );
};

const canArrive = (shift) => {
  const status = String(shift.status || "").toLowerCase();

  return (
    status === "tracking" &&
    !!shift.activatedAt &&
    !shift.arrivedAt &&
    !isExpiredShift(shift) &&
    !isCancelled(shift)
  );
};

  const isRequestedByMe = (shift) =>
    Array.isArray(shift.requestedWorkerUids) && shift.requestedWorkerUids.includes(workerUid);

const isActionableFutureShift = useCallback(
  (shift) => {
    const unresolved = shift?.isFlexibleTime === true || shift?.timeRequired === true || shift?.status === "time_required";
    if (unresolved && !shift?.startTime && !shift?.endTime) return true;

    const startMs = toMillis(shift?.startTime);
    const endMs = toMillis(shift?.endTime);

    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return false;

    // block only when truly ended
    if (endMs <= now) return false;

    return true;
  },
  [now]
);
const canRequestPool = (shift) => {
  const assignedUid = getAssignedUid(shift);
  const mode = getMode(shift);
  const status = String(shift.status || "").toLowerCase();

  const requested = Array.isArray(shift.requestedWorkerUids)
    ? shift.requestedWorkerUids.includes(workerUid)
    : false;

  const requestableStatus =
    status === "open" ||
    (status === "time_required" && shift?.isFlexibleTime === true);

  return (
    isWorkerActive &&
    !isCancelled(shift) &&
    mode === "pool" &&
    requestableStatus &&
    !assignedUid &&
    !requested &&
    !overlapsBlockingShift(shift) &&
    hasValidCredentialForRole(shift.role) &&
    isActionableFutureShift(shift)
  );
};
  const canAcceptOffer = (shift) => {
    const mode = getMode(shift);
    const offered = Array.isArray(shift.offeredWorkerUids) ? shift.offeredWorkerUids : [];
    const status = String(shift.status || "").toLowerCase();

    return (
      isWorkerActive &&
      !isCancelled(shift) &&
      mode === "direct" &&
      (status === "offered" || status === "open") &&
      offered.includes(workerUid) &&
      !overlapsBlockingShift(shift) &&
      hasValidCredentialForRole(shift.role) &&
      isActionableFutureShift(shift)
    );
  };

const requestBlockReason = useCallback(
  (shift) => {
    const assignedUid = getAssignedUid(shift);
    const mode = getMode(shift);
    const status = String(shift.status || "").toLowerCase();

    if (!isWorkerActive) {
      return { text: "Worker inactive", tone: "danger" };
    }

    if (isCancelled(shift)) {
      return { text: "Shift cancelled", tone: "danger" };
    }

    if (mode !== "pool") {
      return { text: "Not a pool shift", tone: "neutral" };
    }

    if (status !== "open" && status !== "time_required") {
      return { text: "Shift not open", tone: "neutral" };
    }

    if (assignedUid) {
      return { text: "Already assigned", tone: "neutral" };
    }

    if (isRequestedByMe(shift)) {
      return { text: "Already requested", tone: "info" };
    }

    if (overlapsBlockingShift(shift)) {
      return { text: "Overlaps existing shift", tone: "warning" };
    }

    if (!hasValidCredentialForRole(shift.role)) {
      return {
        text: `Missing ${shift.role} credential`,
        tone: "warning",
      };
    }

    if (!isActionableFutureShift(shift)) {
      return hasShiftStarted(shift, now)
        ? { text: "Shift already started", tone: "warning" }
        : { text: "Shift expired", tone: "warning" };
    }

    return { text: "Available", tone: "success" };
  },
  [
    isWorkerActive,
    hasValidCredentialForRole,
    overlapsBlockingShift,
    isActionableFutureShift,
    now,
  ]
);

  const requestedPoolShifts = useMemo(() => {
    return (facilityShifts || [])
      .filter((s) => !isCancelled(s))
      .filter((s) => getMode(s) === "pool")
      .filter((s) => String(s.status || "").toLowerCase() === "open")
      .filter((s) => isRequestedByMe(s))
      .sort((a, b) => toMillis(a.startTime) - toMillis(b.startTime));
  }, [facilityShifts, workerUid]);

const openPoolShifts = useMemo(() => {
  const nowMs = now;
  const WINDOW_DAYS = 7;
  const MAX_OPEN = 6;
  const windowEnd = nowMs + WINDOW_DAYS * 24 * 60 * 60 * 1000;

  return (facilityShifts || [])
    .filter((s) => !isCancelled(s))
    .filter((s) => getMode(s) === "pool")
    .filter((s) => {
      const status = String(s.status || "").toLowerCase();
      return status === "open" || status === "time_required";
    })
    .filter((s) => hasValidCredentialForRole(s.role))
    .filter(
      (s) =>
        !(Array.isArray(s.declinedWorkerUids) &&
          s.declinedWorkerUids.includes(workerUid))
    )
    .filter((s) => !getAssignedUid(s))
    .filter((s) => !isRequestedByMe(s))
    .filter((s) => {
      if (s?.isFlexibleTime === true || s?.timeRequired === true) return isActionableFutureShift(s);

      const st = toMillis(s.startTime);
      const en = toMillis(s.endTime);

      if (!Number.isFinite(st) || !Number.isFinite(en)) return false;
      if (en <= nowMs) return false;
      if (st > windowEnd) return false;

      return true;
    })
    .filter((s) => !overlapsBlockingShift(s))
    .sort((a, b) => {
      const aFlexible = a?.isFlexibleTime === true || a?.timeRequired === true;
      const bFlexible = b?.isFlexibleTime === true || b?.timeRequired === true;

      if (aFlexible && !bFlexible) return 1;
      if (!aFlexible && bFlexible) return -1;

      return toMillis(a.startTime) - toMillis(b.startTime);
    })
    .slice(0, MAX_OPEN);
}, [facilityShifts, workerUid, now, overlapsBlockingShift, hasValidCredentialForRole, isActionableFutureShift]);

 const myShiftsVisible = useMemo(
  () =>
    (myShifts || [])
      .filter((s) => !isCancelled(s))
      .filter((s) => {
        const status = String(s.status || "").toLowerCase();

        // keep completed/ended for History
        if (["completed", "ended"].includes(status)) return true;

        // keep live shifts even after end until planner completes them
        if (["assigned", "activated", "tracking", "arrived"].includes(status)) return true;

        return !isExpiredShift(s);
      })
      .sort((a, b) => toMillis(a.startTime) - toMillis(b.startTime)),
  [myShifts, isExpiredShift]
);

  const todayStart = useMemo(() => {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, [now]);

  const tomorrowStart = useMemo(() => {
    const d = new Date(now);
    d.setHours(24, 0, 0, 0);
    return d.getTime();
  }, [now]);

const todayShifts = useMemo(() => {
  return myShiftsVisible
    .filter((s) => !["completed", "ended"].includes(String(s.status || "").toLowerCase()))
    .filter((s) => {
      const start = toMillis(s.startTime);
      const end = toMillis(s.endTime);
      if (!Number.isFinite(start) || !Number.isFinite(end)) return false;
      return isSameDayMs(start, todayStart, tomorrowStart) || (start <= now && end >= now);
    });
}, [myShiftsVisible, todayStart, tomorrowStart, now]);

  const upcomingShifts = useMemo(() => {
    return myShiftsVisible.filter((s) => {
      const start = toMillis(s.startTime);
      if (!Number.isFinite(start)) return false;
      return start > now;
    });
  }, [myShiftsVisible, now]);

const historyPendingPay = useMemo(() => {
  return (myShifts || [])
    .filter((s) => {
      const status = String(s.status || "").toLowerCase();
      const ended = hasShiftEnded(s, now);

      return (
        status === "completed" ||
        status === "ended" ||
        (ended && ["assigned", "activated", "tracking", "arrived"].includes(status))
      );
    })
    .filter((s) => shiftPaymentStatus(s) !== "paid")
    .sort(
      (a, b) =>
        toMillis(b.completedAt || b.endTime) -
        toMillis(a.completedAt || a.endTime)
    );
}, [myShifts, now]);

const historyPaid = useMemo(() => {
  const nowMs = now;
  const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;

  return (myShifts || [])
    .filter((s) => String(s.status || "").toLowerCase() === "completed")
    .filter((s) => shiftPaymentStatus(s) === "paid")
    .filter((s) => {
      const paidMs = toMillis(s.paidAt || s.updatedAt || s.completedAt || s.endTime);
      return Number.isFinite(paidMs) && nowMs - paidMs <= thirtyDaysMs;
    })
    .sort(
      (a, b) =>
        toMillis(b.paidAt || b.updatedAt || b.completedAt || b.endTime) -
        toMillis(a.paidAt || a.updatedAt || a.completedAt || a.endTime)
    );
}, [myShifts, now]);

  const offerCount =
    (myOffers?.length || 0) + requestedPoolShifts.length + openPoolShifts.length;
  const activeCount = myShiftsVisible.filter((s) =>
    ["assigned", "activated", "tracking", "arrived"].includes(String(s.status || "").toLowerCase())
  ).length;
  const hasActiveMyShift = myShiftsVisible.length > 0;

  const heroShiftId = useMemo(() => {
    const windowMs = activationWindowHours * 60 * 60 * 1000;

    const eligible = (myShiftsVisible || [])
      .filter((s) =>
        ["assigned", "activated", "tracking", "arrived"].includes(String(s.status || "").toLowerCase())
      )
      .map((s) => ({ ...s, startMs: toMillis(s.startTime), endMs: toMillis(s.endTime) }))
      .filter((s) => Number.isFinite(s.startMs) && Number.isFinite(s.endMs))
      .filter((s) => s.endMs > now)
  .filter((s) => {
  const activationOpenAt = s.startMs - windowMs;
  return now >= activationOpenAt && now < s.endMs;
})
      .sort((a, b) => a.startMs - b.startMs);

    return eligible[0]?.id || null;
  }, [myShiftsVisible, now, activationWindowHours]);


useEffect(() => {
  let cancelledEffect = false;

  if (lastHeroRef.current !== heroShiftId) {
    insideCountRef.current = 0;
    lastHeroRef.current = heroShiftId;
  }

  (async () => {
    try {
      if (!workerUid) return;

      if (!heroShiftId) {
        await stopTracking();
        return;
      }

      const heroShiftLatest = (myShiftsVisible || []).find((s) => s.id === heroShiftId);
      const assignedUidLatest = getAssignedUid(heroShiftLatest);

      if (!heroShiftLatest || assignedUidLatest !== workerUid) {
        await stopTracking();
        return;
      }

 const status = String(
  heroShiftLatest.status || ""
).toLowerCase();

/*
 * Tracking must begin only after the worker
 * explicitly presses "I'm On My Way".
 *
 * ACTIVATE and ON MY WAY are separate
 * ShiftBridge visibility milestones.
 */
if (status !== "tracking") {
  await stopTracking();
  return;
}

await ensurePermissions();

await startTrackingForShift({
        shiftId: heroShiftId,
        onLocation: async (loc) => {
          if (cancelledEffect) return;
try {
  const lat = loc?.latitude ?? null;
  const lng = loc?.longitude ?? null;
  const accuracy = loc?.accuracy ?? null;

  if (lat == null || lng == null) return;

  await addDoc(collection(db, "shifts", heroShiftId, "locations"), {
    lat,
    lng,
    accuracy,
    capturedAt: serverTimestamp(),
    workerUid,
  });

  const heroShift = (myShiftsVisible || []).find(
    (s) => s.id === heroShiftId
  );

  if (!heroShift) return;

  const currentStatus = String(
    heroShift.status || ""
  ).toLowerCase();

  if (!["activated", "tracking"].includes(currentStatus)) {
    insideCountRef.current = 0;
    return;
  }

  if (
    heroShift.arrivedAt ||
    currentStatus === "arrived"
  ) {
    return;
  }

  if (
    facilityGeo.lat == null ||
    facilityGeo.lng == null ||
    !Number.isFinite(facilityGeo.radius)
  ) {
    return;
  }

  const dist = haversineMeters(
    lat,
    lng,
    facilityGeo.lat,
    facilityGeo.lng
  );

  if (dist <= facilityGeo.radius) {
    insideCountRef.current += 1;
  } else {
    insideCountRef.current = 0;
  }

  if (insideCountRef.current >= 2) {
    insideCountRef.current = 0;

   await setShiftArrived(heroShiftId, workerUid, {
  method: "gps",
  distanceMeters: dist,
  accuracyMeters: accuracy,
});
  }
} catch (e) {
  console.error("Geo ping / arrival failed:", e);
}


        },
      });
    } catch (e) {
      console.error("Auto geo tracking failed:", e);
    }
  })();

  return () => {
    cancelledEffect = true;
  };
}, [
  heroShiftId,
  workerUid,
  now,
  myShiftsVisible,
  facilityGeo.lat,
  facilityGeo.lng,
  facilityGeo.radius,
]);

  const expiredCreds = useMemo(() => {
    const nowMs = Date.now();
    return workerCreds.filter((c) => {
      if (!c.expiresAt) return false;
      try {
        return c.expiresAt.toDate().getTime() < nowMs;
      } catch {
        return false;
      }
    });
  }, [workerCreds]);

  const surfaceText = (shift, countdown) => {
if (
  shift?.isFlexibleTime === true &&
  !shift?.startTime
) {
  return "Schedule to be confirmed";
}
    const startMs = toMillis(shift.startTime);
    const endMs = toMillis(shift.endTime);
    const ended = hasShiftEnded(shift, now);
    if (ended) return "Shift ended";

    const hasStarted = Number.isFinite(startMs) && now >= startMs;
    const notEnded = Number.isFinite(endMs) && now < endMs;

    if ((shift.arrivedAt || shift.activatedAt || hasStarted) && notEnded) {
      return formatElapsed(shift.startTime, now);
    }

    const startsSoon = formatStartsIn(shift.startTime, now);
    if (startsSoon) return startsSoon;

    return countdown.principal || "";
  };

const shellStyle = {
  width: "100%",
  maxWidth: 430,
  margin: "0 auto",
  minHeight: "100vh",
  background: "#f8fafc",
  padding: 12,
  boxSizing: "border-box",
  boxShadow: "0 6px 14px rgba(15,23,42,.04)",
};

const statusTone = (shift, lifecycle) => {
  const ended = hasShiftEnded(shift, now);
  const statusName = String(shift?.status || "open").toLowerCase();
  const arrived = !!shift?.arrivedAt || statusName === "arrived";

  if (statusName === "completed") {
    return {
      label: "COMPLETED",
      bg: "#dcfce7",
      border: "#86efac",
      text: "#166534",
    };
  }

  if (ended && ["assigned", "activated", "tracking", "arrived"].includes(statusName)) {
  return {
    label: "READY TO COMPLETE",
    bg: "#fef3c7",
    border: "#facc15",
    text: "#92400e",
  };
}

if (ended) {
  return {
    label: "ENDED",
      bg: "#f1f5f9",
      border: "#cbd5e1",
      text: "#475569",
    };
  }

  if (arrived) {
    return {
      label: "ARRIVED",
      bg: "#dcfce7",
      border: "#86efac",
      text: "#166534",
    };
  }

  if (statusName === "tracking") {
    return {
      label: "TRACKING",
      bg: "#f5f3ff",
      border: "#c4b5fd",
      text: "#6d28d9",
    };
  }

  if (statusName === "activated") {
    return {
      label: "ACTIVATED",
      bg: "#dbeafe",
      border: "#93c5fd",
      text: "#1d4ed8",
    };
  }

  if (statusName === "assigned") {
    return {
      label: "ASSIGNED",
      bg: "#eff6ff",
      border: "#bfdbfe",
      text: "#1e40af",
    };
  }

  if (statusName === "offered") {
    return {
      label: "OFFERED",
      bg: "#fff7ed",
      border: "#fdba74",
      text: "#9a3412",
    };
  }

  if (lifecycle === "late") {
    return {
      label: String(shift?.status || "late").toUpperCase(),
      bg: "#fee2e2",
      border: "#fca5a5",
      text: "#991b1b",
    };
  }

  if (lifecycle === "critical") {
    return {
      label: String(shift?.status || "critical").toUpperCase(),
      bg: "#ffedd5",
      border: "#fdba74",
      text: "#9a3412",
    };
  }

  return {
    label: String(shift?.status || "open").toUpperCase(),
    bg: "#f8fafc",
    border: "#d7dee8",
    text: "#334155",
  };
};

  
const workerCardContext = {
  actionFeedback,
  activationWindowHours,
  cardShellStyle,
  computeShiftLifecycle,
  expandedShiftId,
  facilityId,
  formatDateTime: (value, shift) => formatShiftInstant(value, shiftTimeZone(shift), {year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit"}),
  formatTime: (value, shift) => formatShiftInstant(value, shiftTimeZone(shift), {hour: "numeric", minute: "2-digit"}),
  getMode,
  hasShiftEnded,
  now,
  roleColorFor,
  setExpandedShiftId,
  statusTone,
  workerProfile,
};

const renderAssignedShiftActions = (shift) => {
  if (isStaleUnfinishedShift(shift, now)) {
    return null;
  }

  const status = String(
    shift?.status || ""
  ).toLowerCase();

  const accepted =
    shift.assignmentAccepted === true ||
    shift.acceptedByWorker === true ||
    !!shift.acceptedAt;

  const acceptBusy =
    actionBusyId ===
    `accept-assigned-${shift.id}`;

  const declineBusy =
    actionBusyId ===
    `decline-assigned-${shift.id}`;

  const activateBusy =
    actionBusyId ===
    `activate-${shift.id}`;

  const trackingBusy =
    actionBusyId ===
    `tracking-${shift.id}`;

  const arriveBusy =
    actionBusyId ===
    `arrive-${shift.id}`;

  const completeBusy =
    actionBusyId ===
    `complete-${shift.id}`;

  const primaryButton = {
    border: "1px solid #2563eb",
    background: "#2563eb",
    color: "#ffffff",
    borderRadius: 10,
    padding: "9px 12px",
    fontWeight: 900,
    fontSize: 12,
    cursor: "pointer",
  };

  const secondaryButton = {
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    borderRadius: 10,
    padding: "9px 12px",
    fontWeight: 900,
    fontSize: 12,
    cursor: "pointer",
  };

  if (
    status === "assigned" &&
    !accepted
  ) {
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "1fr 1fr",
          gap: 8,
          width: "100%",
        }}
      >
        <button
          type="button"
          disabled={
            acceptBusy ||
            declineBusy
          }
          onClick={() =>
            handleAcceptAssignedShift(
              shift
            )
          }
          style={{
            ...primaryButton,
            opacity:
              acceptBusy ||
              declineBusy
                ? 0.6
                : 1,
          }}
        >
          {acceptBusy
            ? "Accepting..."
            : "Accept"}
        </button>

        <button
          type="button"
          disabled={
            acceptBusy ||
            declineBusy
          }
          onClick={() =>
            handleDeclineAssignedShift(
              shift
            )
          }
          style={{
            ...secondaryButton,
            color: "#b91c1c",
            borderColor: "#fecaca",
            opacity:
              acceptBusy ||
              declineBusy
                ? 0.6
                : 1,
          }}
        >
          {declineBusy
            ? "Declining..."
            : "Decline"}
        </button>
      </div>
    );
  }

  if (
    status === "assigned" &&
    accepted
  ) {
    if (canActivate(shift)) {
      return (
        <button
          type="button"
          disabled={activateBusy}
          onClick={async () => {
            try {
              setActionBusyId(
                `activate-${shift.id}`
              );

              await handleActivate(
                shift.id
              );
            } finally {
              setActionBusyId("");
            }
          }}
          style={{
            ...primaryButton,
            width: "100%",
            opacity:
              activateBusy
                ? 0.6
                : 1,
          }}
        >
          {activateBusy
            ? "Activating..."
            : "Activate Shift"}
        </button>
      );
    }

    const activationOpenAt =
      toMillis(
        shift?.startTime ||
          shift?.start
      ) -
      activationWindowHours *
        60 *
        60 *
        1000;

    return actionReasonPill(
      Number.isFinite(
        activationOpenAt
      ) &&
        now <
          activationOpenAt
        ? `Accepted — activation opens ${formatDateTime(
            activationOpenAt, shiftTimeZone(shift)
          )}`
        : "Accepted — waiting for activation",
      "info"
    );
  }

  if (status === "activated") {
    return (
      <button
        type="button"
        disabled={trackingBusy}
        onClick={() =>
          handleStartTracking(
            shift.id
          )
        }
        style={{
          ...primaryButton,
          width: "100%",
          opacity:
            trackingBusy
              ? 0.6
              : 1,
        }}
      >
        {trackingBusy
          ? "Updating..."
          : "I'm On My Way"}
      </button>
    );
  }

  if (status === "tracking") {
    return (
      <button
        type="button"
        disabled={arriveBusy}
        onClick={async () => {
          try {
            setActionBusyId(
              `arrive-${shift.id}`
            );

            await handleArrived(
              shift.id
            );
          } finally {
            setActionBusyId("");
          }
        }}
        style={{
          ...primaryButton,
          width: "100%",
          background:
            "#16a34a",
          borderColor:
            "#16a34a",
          opacity:
            arriveBusy
              ? 0.6
              : 1,
        }}
      >
        {arriveBusy
          ? "Updating..."
          : "I've Arrived"}
      </button>
    );
  }

  if (status === "arrived") {
    const endMs = toMillis(
      shift?.endTime ||
        shift?.end
    );

    const completionAvailable =
      Number.isFinite(endMs) &&
      now >= endMs;

    if (!completionAvailable) {
      return actionReasonPill(
        Number.isFinite(endMs)
          ? `Complete shift available after ${formatDateTime(
              endMs, shiftTimeZone(shift)
            )}`
          : "Shift completion unavailable until the scheduled end time",
        "info"
      );
    }

    return (
      <button
        type="button"
        disabled={completeBusy}
        onClick={async () => {
          try {
            setActionBusyId(
              `complete-${shift.id}`
            );

            await handleComplete(
              shift.id
            );
          } finally {
            setActionBusyId("");
          }
        }}
        style={{
          ...primaryButton,
          width: "100%",
          background:
            "#16a34a",
          borderColor:
            "#16a34a",
          opacity:
            completeBusy
              ? 0.6
              : 1,
        }}
      >
        {completeBusy
          ? "Completing..."
          : "Complete Shift"}
      </button>
    );
  }

  if (status === "completed") {
    return actionReasonPill(
      "Shift completed",
      "info"
    );
  }

  return null;
};

const offerBlockReason = useCallback(
  (shift) => {
    const status = String(
      shift?.status || ""
    ).toLowerCase();

    const offeredWorkerUids =
      Array.isArray(
        shift?.offeredWorkerUids
      )
        ? shift.offeredWorkerUids
        : [];

    if (!isWorkerActive) {
      return {
        text: "Worker inactive",
        tone: "danger",
      };
    }

    if (isCancelled(shift)) {
      return {
        text: "Shift cancelled",
        tone: "danger",
      };
    }

    if (getMode(shift) !== "direct") {
      return {
        text: "Not a direct offer",
        tone: "neutral",
      };
    }

    if (
      !offeredWorkerUids.includes(
        workerUid
      )
    ) {
      return {
        text: "Not offered to you",
        tone: "neutral",
      };
    }

    if (
      !["offered", "open"].includes(
        status
      )
    ) {
      return {
        text: "Offer no longer available",
        tone: "neutral",
      };
    }

    if (overlapsBlockingShift(shift)) {
      return {
        text: "Overlaps existing shift",
        tone: "warning",
      };
    }

    if (
      !hasValidCredentialForRole(
        shift.role
      )
    ) {
      return {
        text: `Missing ${shift.role} credential`,
        tone: "warning",
      };
    }

    if (
      !isActionableFutureShift(
        shift
      )
    ) {
      return {
        text: "Shift expired",
        tone: "warning",
      };
    }

    return {
      text: "Available",
      tone: "info",
    };
  },
  [
    isWorkerActive,
    workerUid,
    getMode,
    overlapsBlockingShift,
    hasValidCredentialForRole,
    isActionableFutureShift,
  ]
);
const renderOfferCardActions = (shift, kind) => {
  const acceptBusy = actionBusyId === `accept-${shift.id}`;
  const requestBusy = actionBusyId === `request-${shift.id}`;
  const withdrawBusy = actionBusyId === `withdraw-${shift.id}`;

  const primaryBtn = {
    borderRadius: 999,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 900,
    background: "#2563eb",
    color: "#ffffff",
    border: "1px solid #2563eb",
    boxShadow: "0 8px 18px rgba(37,99,235,.22)",
    opacity: 1,
    cursor: "pointer",
  };

  const secondaryBtn = {
    borderRadius: 999,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 900,
    background: "#ffffff",
    color: "#1d4ed8",
    border: "1px solid #bfdbfe",
  };

  // 🔵 DIRECT OFFER
if (kind === "direct") {
  if (canAcceptOffer(shift)) {
    const declineBusy =
      actionBusyId === `decline-${shift.id}`;

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
          width: "100%",
        }}
      >
        <button
          style={{
            ...primaryBtn,
            opacity:
              acceptBusy || declineBusy ? 0.6 : 1,
          }}
          disabled={acceptBusy || declineBusy}
          onClick={() => handleAcceptOffer(shift.id)}
        >
          {acceptBusy ? "Working..." : "Accept"}
        </button>

        <button
          style={{
            ...secondaryBtn,
            color: "#b91c1c",
            borderColor: "#fecaca",
            opacity:
              acceptBusy || declineBusy ? 0.6 : 1,
          }}
          disabled={acceptBusy || declineBusy}
          onClick={() => handleDeclineOffer(shift.id)}
        >
          {declineBusy ? "Working..." : "Decline"}
        </button>
      </div>
    );
  }

  const reason = offerBlockReason(shift);
  return actionReasonPill(reason.text, reason.tone);
}

  // 🟡 REQUESTED (already sent)
  if (kind === "requested") {
    return (
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {actionReasonPill("Requested", "info")}

        <button
          style={{ ...secondaryBtn, opacity: withdrawBusy ? 0.6 : 1 }}
          disabled={withdrawBusy}
          onClick={() => {
            const ok = window.confirm("Withdraw your request?");
            if (!ok) return;
            setActionBusyId(`withdraw-${shift.id}`);
            typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shift.id]:{phase:'pending',label:'Withdrawing request',beforeStage:getVisibilityStage(shift)}}));
            Promise.resolve().then(() => declineShift(shift.id, workerUid)).then(() => typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shift.id]:{...previous[shift.id],phase:'waiting'}}))).catch(error => typeof setActionFeedback === 'function' && setActionFeedback(previous=>({...previous,[shift.id]:{...previous[shift.id],phase:'failed',error:error?.message}}))).finally(() =>
              setActionBusyId("")
            );
          }}
        >
          {withdrawBusy ? "Working..." : "Withdraw"}
        </button>
      </div>
    );
  }

  // 🟢 POOL SHIFT
  if (kind === "pool") {
    if (canRequestPool(shift)) {
      return (
        <button
          style={{ ...primaryBtn, opacity: requestBusy ? 0.6 : 1 }}
          disabled={requestBusy}
          onClick={() => handlePoolRequest(shift.id)}
        >
          {requestBusy ? "Working..." : "Request"}
        </button>
      );
    }

    if (isRequestedByMe(shift)) {
      return actionReasonPill("Requested", "info");
    }

    const reason = requestBlockReason(shift);
    return actionReasonPill(reason.text, reason.tone);
  }

  return null;
};

const handleAvailabilityStatusChange = async (nextStatus) => {
  if (!workerDocId) {
    alert("No worker profile document found.");
    setAvailabilityStatus(workerProfile?.availabilityStatus || "available");
    return;
  }

  try {
    setAvailabilitySaving(true);
    setAvailabilityStatus(nextStatus);

    await updateDoc(doc(db, "users", workerDocId), {
      availabilityStatus: nextStatus,
      availabilityUpdatedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (err) {
    console.error("Availability status update failed:", err);

    setAvailabilityStatus(workerProfile?.availabilityStatus || "available");

    alert(err?.message || "Availability update failed.");
  } finally {
    setAvailabilitySaving(false);
  }
};




function isClosedShiftStatus(shift) {
  const status = String(shift?.status || "").toLowerCase();

  return [
    "completed",
    "no_show",
    "cancelled",
    "canceled",
    "closed_unfilled",
  ].includes(status);
}

const myShiftsVisibleActive = myShiftsVisible.filter(
  (s) => !isClosedShiftStatus(s)
);

const todayShiftsActive = todayShifts.filter(
  (s) => !isClosedShiftStatus(s)
);

const upcomingShiftsActive = upcomingShifts.filter(
  (s) => !isClosedShiftStatus(s)
);

  const assignedCard = (
    <div
      style={{
        ...cardShellStyle(),
        border: hasActiveMyShift ? "1px solid #bfdbfe" : "1px solid #d8e1ea",
        boxShadow: hasActiveMyShift
          ? "0 0 0 4px rgba(37,99,235,.05), 0 16px 34px rgba(15,23,42,.07)"
          : "0 8px 22px rgba(15,23,42,.05)",
      }}
    >
      <div style={cardHeaderStyle()}>
        <div
          style={{
            fontWeight: 900,
            fontSize: 16,
            color: "#166534",
            letterSpacing: -0.2,
            background: "#ecfdf5",
            border: "1px solid #86efac",
            borderRadius: 999,
            padding: "6px 12px",
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          Assigned / active
        </div>
        <div style={countPillStyle()}>{myShiftsVisibleActive.length}</div>
      </div>

      <div style={{ padding: 18 }}>
        {myShiftsVisibleActive.length === 0 ? (
          <div style={{ color: "#64748b", fontSize: 14, lineHeight: 1.5 }}>
            No assigned shifts.
          </div>
        ) : (
          myShiftsVisibleActive.map((shift) => {
            const isHero = heroShiftId && shift.id === heroShiftId;
            return (
              <ShiftCard context={workerCardContext}
                key={shift.id}
                shift={shift}
                emphasized={isHero}
               actions={{
  extra: renderAssignedShiftActions(shift),
}}
              />
            );
          })
        )}
      </div>
    </div>
  );

  const todayCard = (
    <div style={cardShellStyle()}>
      <div style={cardHeaderStyle()}>
        <div
          style={{
            fontWeight: 900,
            fontSize: 16,
            color: "#7c3aed",
            letterSpacing: -0.2,
            background: "#f5f3ff",
            border: "1px solid #c4b5fd",
            borderRadius: 999,
            padding: "6px 12px",
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          Today
        </div>
        <div style={countPillStyle()}>{todayShiftsActive.length}</div>
      </div>

      <div style={{ padding: 18 }}>
        {todayShiftsActive.length === 0 ? (
          <div style={{ color: "#64748b", fontSize: 14, lineHeight: 1.5 }}>
            No assigned shifts today.
          </div>
        ) : (
          todayShiftsActive.map((shift) => (
            <ShiftCard context={workerCardContext}
              key={shift.id}
              shift={shift}
              emphasized={heroShiftId === shift.id}
            actions={{
  extra: renderAssignedShiftActions(shift),
}}
            />
          ))
        )}
      </div>
    </div>
  );

  const upcomingCard = (
    <div style={cardShellStyle()}>
      <div style={cardHeaderStyle()}>
        <div
          style={{
            fontWeight: 900,
            fontSize: 16,
            color: "#9a3412",
            letterSpacing: -0.2,
            background: "#fff7ed",
            border: "1px solid #fdba74",
            borderRadius: 999,
            padding: "6px 12px",
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          Upcoming
        </div>
        <div style={countPillStyle()}>{upcomingShiftsActive.length}</div>
      </div>

      <div style={{ padding: 18 }}>
        {upcomingShiftsActive.length === 0 ? (
          <div style={{ color: "#64748b", fontSize: 14, lineHeight: 1.5 }}>
            No upcoming assigned shifts.
          </div>
        ) : (
          upcomingShiftsActive.map((shift) => (
            <ShiftCard context={workerCardContext}
              key={shift.id}
              shift={shift}
            actions={{
  extra: renderAssignedShiftActions(shift),
}}
            />
          ))
        )}
      </div>
    </div>
  );

  const historyCard = (
    (<section className="we-surface"><h2>Shift history</h2>{historyPendingPay.length + historyPaid.length === 0 ? <p>No past shifts.</p> : [...historyPendingPay, ...historyPaid].map(shift=><ShiftCard key={shift.id} shift={shift} context={workerCardContext}/>)}</section>)
  );



const workerNeedsReupload =
  workerProfile?.onboardingStatus === "reupload_required" ||
  Object.values(workerProfile?.credentialMap || {}).some(
    (cred) => cred?.status === "reupload_required"
  );

const workerApproved = compliance.canWork === true;
  const workerOperationalState = useMemo(() => {
  return getWorkerOperationalState(workerProfile || {});
}, [workerProfile]);

if (requiresOnboarding) {
  return (
    <WorkerOnboarding
      user={auth.currentUser}
      userProfile={workerProfile}
      profile={workerProfile}
    />
  );
}

  const pastOffers = myOffers.filter(s => hasShiftEnded(s, now) || s.active === false || ['cancelled', 'closed', 'completed', 'no_show'].includes(String(s.status || '').toLowerCase()));
  const actionableOffers = myOffers.filter(s => !pastOffers.includes(s));
  const currentShifts = myShiftsVisibleActive.filter(s => !isStaleUnfinishedShift(s, now));
  const focusShift = (currentShifts.find(s => ['activated','tracking','arrived'].includes(getVisibilityStage(s))) || currentShifts.find(s => s.id === heroShiftId) || currentShifts.find(s => !hasShiftEnded(s, now)));
return <WorkerWorkspace tenantId={tenantId} workerUid={workerUid} loading={workerShiftsLoading} unavailable={Boolean(listenerError)} identity={<>{auth.currentUser?.email}{facilityId ? ' · '+facilityId : ''}</>} workerState={workerOperationalState.label} exit={<button
              className="sb-btn sb-btn-outline"
              style={{
                padding: "10px 16px",
                borderRadius: 10,
                fontSize: 14,
                lineHeight: 1,
                minHeight: 42,
                background: "#fff",
                color: "#0f172a",
                border: "1px solid #d8e1ea",
                fontWeight: 900,
              }}
              onClick={() => {
                const hasLiveShift = (myShiftsVisible || []).some((shift) => {
                  const status = String(shift.status || "").toLowerCase();
                  return ["assigned", "activated", "tracking", "arrived"].includes(status);
                });

                if (hasLiveShift) {
                  const ok = window.confirm(
                    "You have a live shift in progress or upcoming. Log out anyway?"
                  );
                  if (!ok) return;
                }

                signOut(auth);
              }}
            >
              Sign out
            </button>} notices={<>{listenerError && <div className="we-notice" role="alert"><strong>Data feed error</strong><div>{listenerError}</div></div>}{expiredCreds.map((c,idx)=><div className="we-notice" role="alert" key={idx}>Credential {c.type} expired ? Contact Admin</div>)}</>} home={focusShift ? <ShiftCard context={workerCardContext} shift={focusShift} emphasized actions={{extra:renderAssignedShiftActions(focusShift)}}/> : null} direct={<div>
        <div style={{ fontWeight: 900, marginBottom: 10, color: "#475569", fontSize: 14 }}>
          Direct offers
        </div>

        {actionableOffers.length === 0 ? (
          <div style={{ color: "#64748b", fontSize: 14, lineHeight: 1.5 }}>
            No direct offers right now.
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr"
              
            }}
          >
            {actionableOffers.map((shift) => (
              <ShiftCard context={workerCardContext}
                key={shift.id}
                shift={shift}
                banner="Offered to you"
                actions={{
                  extra: renderOfferCardActions(shift, "direct"),
                }}
              />
            ))}
          </div>
        )}
        {pastOffers.length > 0 && <details className="we-past-offers"><summary>Past offers ({pastOffers.length})</summary><p>These offers have ended or closed. They are kept here for reference.</p>{pastOffers.map(shift => <ShiftCard key={shift.id} context={workerCardContext} shift={shift} banner="Past offer" />)}</details>}
      </div>} requests={<div>
        <div style={{ fontWeight: 900, marginBottom: 10, color: "#475569", fontSize: 14 }}>
          Requested
        </div>

        {requestedPoolShifts.length === 0 ? (
          <div style={{ color: "#64748b", fontSize: 14, lineHeight: 1.5 }}>
            No requested pool shifts pending right now.
          </div>
        ) : (
          <div
            style={{
              display: "grid",
             gridTemplateColumns: "1fr"
            }}
          >
            {requestedPoolShifts.map((shift) => (
              <ShiftCard context={workerCardContext}
                key={shift.id}
                shift={shift}
                banner="Requested"
                actions={{
                  extra: renderOfferCardActions(shift, "requested"),
                }}
              />
            ))}
          </div>
        )}
      </div>} pool={<div>
        <div style={{ fontWeight: 900, marginBottom: 10, color: "#475569", fontSize: 14 }}>
          Available pool shifts
        </div>

        {openPoolShifts.length === 0 ? (
          <div style={{ color: "#64748b", fontSize: 14, lineHeight: 1.5 }}>
            No pool shifts available right now.
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr"
            }}
          >
            {openPoolShifts.map((shift) => (
              <ShiftCard context={workerCardContext}
                key={shift.id}
                shift={shift}
                banner="Pool shift"
                actions={{
                  extra: renderOfferCardActions(shift, "pool"),
                }}
              />
            ))}
          </div>
        )}
      </div>} today={todayCard} upcoming={upcomingCard} assigned={assignedCard} history={historyCard} profile={<div style={{ display: "grid", gap: 14 }}>
      <div style={cardShellStyle()}>
        <div style={cardHeaderStyle()}>
          <div style={{ fontWeight: 900, fontSize: 16, color: "#0f172a" }}>
            Availability
          </div>
        </div>

        <div style={{ padding: 18, display: "grid", gap: 14 }}>
          <div style={{ fontWeight: 900, color: "#475569" }}>
            Quick Status — your current operating status
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
            }}
          >
            {AVAILABILITY_STATUS_OPTIONS.map((opt) => {
              const active = availabilityStatus === opt.key;

              return (
                <button
                  key={opt.key}
                  type="button"
                  disabled={availabilitySaving}
                  onClick={() => handleAvailabilityStatusChange(opt.key)}
                  style={{
                    borderRadius: 14,
                    padding: "12px 10px",
                    fontWeight: 900,
                    fontSize: 13,
                    border: active
                      ? "2px solid #2563eb"
                      : "1px solid #d8e1ea",
                    background: active ? "#eff6ff" : "#ffffff",
                    color: active ? "#1d4ed8" : "#334155",
                    cursor: availabilitySaving ? "wait" : "pointer",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          <div
            style={{
              border: "1px solid #d8e1ea",
              background: "#f8fafc",
              borderRadius: 14,
              padding: 12,
              color: "#475569",
              fontSize: 13,
              fontWeight: 800,
            }}
          >
            {availabilitySaving ? "Saving status—" : "Status saves automatically."}<br />Current status:{" "}
            <span style={{ color: "#0f172a", fontWeight: 900 }}>
              {availabilityStatusLabel(availabilityStatus)}
            </span>
          </div>

          <WeeklyAvailability key={workerUid} worker={workerProfile || {}} onSave={schedule => saveWeeklyAvailability(workerUid, schedule)} />
        </div>
      </div>
    </div>}/>;
}
